/**
 * PlannerCanvas: the root Konva Stage for the floor plan editor. Hosts the
 * room background, every placed object (via ObjectRenderer), and the
 * shared SelectionTransformer.
 *
 * Responsibilities kept deliberately here, not pushed into child shapes:
 *   - Stage sizing (responsive to container, capped zoom)
 *   - Click-on-empty-space deselects (PRD-implicit: clicking nothing
 *     should clear selection, same as Canva/Figma-style tools)
 *   - Shift-click on a shape toggles it into/out of a multi-selection
 *     (PlannerCanvas owns this because it's the thing that sees the raw
 *     click event with modifier keys; ObjectRenderer just reports "this
 *     shape was clicked")
 *   - Pan via stage drag when no object is being dragged
 *   - Zoom via scroll/pinch or toolbar buttons, clamped to a sane range,
 *     plus "fit to screen" and "reset zoom" conveniences
 *   - Room view rotation (landscape/portrait) — a pure display rotation
 *     applied to the rendered content only; see ROOM ROTATION note below
 *
 * ROOM ROTATION — important architectural note:
 * Rotating the room view does NOT touch any stored data. Object x/y/
 * rotation values, and the room's real widthFt/lengthFt, are completely
 * untouched — this component only changes how the existing, unmodified
 * scene is DRAWN, by rotating the outer content Group 90 degrees. This
 * means object positions remain correct in storage and in every other
 * context (export, bride view) regardless of what view rotation a
 * coordinator happens to have toggled on while editing. Per the editor
 * polish brief, exports always render in standard (unrotated)
 * orientation rather than trying to track this view-only preference.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { Stage, Layer, Group } from 'react-konva';
import Konva from 'konva';
import { useEditorStore } from '../../store/useEditorStore';
import type { ObjectCatalogEntry, RoomDefinition } from '../../domain/types';
import { RoomBackground } from './RoomBackground';
import { ObjectRenderer } from './ObjectRenderer';
import { SelectionTransformer } from './SelectionTransformer';
import { FEET_TO_PX } from './canvasConstants';

const MIN_SCALE = 0.2;
const MAX_SCALE = 3;
const SCALE_STEP = 1.05;
const SCREEN_MARGIN_PX = 32; // breathing room around the room when fitting to screen
const ROTATION_TWEEN_DURATION_SEC = 0.35; // quick enough to feel responsive, slow enough to read as intentional motion

export type RoomOrientation = 'standard' | 'rotated';

interface PlannerCanvasProps {
  room: RoomDefinition;
  catalog: ObjectCatalogEntry[];
  width: number;
  height: number;
  orientation?: RoomOrientation;
}

export function PlannerCanvas({ room, catalog, width, height, orientation = 'standard' }: PlannerCanvasProps) {
  const stageRef = useRef<Konva.Stage>(null);
  const backgroundGroupRef = useRef<Konva.Group>(null);
  const objectsGroupRef = useRef<Konva.Group>(null);
  const objects = useEditorStore((state) => state.objects);
  const clearSelection = useEditorStore((state) => state.clearSelection);
  const toggleSelection = useEditorStore((state) => state.toggleSelection);

  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const hasAutoFitRef = useRef(false);
  // Tracks the orientation actually being displayed right now, separate
  // from the `orientation` prop — the prop changes instantly when the
  // toolbar button is clicked, but the Group nodes themselves animate
  // toward it over ROTATION_TWEEN_DURATION_SEC. Keeping the previous
  // value lets the tween effect below know which direction it's
  // animating FROM.
  const displayedOrientationRef = useRef<RoomOrientation>(orientation);

  const roomWidthPx = room.widthFt * FEET_TO_PX;
  const roomHeightPx = room.lengthFt * FEET_TO_PX;
  // When rotated, the room's effective on-screen footprint swaps width/height —
  // needed both for fit-to-screen math and for centering the rotated group.
  const effectiveWidthPx = orientation === 'rotated' ? roomHeightPx : roomWidthPx;
  const effectiveHeightPx = orientation === 'rotated' ? roomWidthPx : roomHeightPx;

  const fitToScreen = useCallback(() => {
    if (width <= 0 || height <= 0) return;
    const availableWidth = width - SCREEN_MARGIN_PX * 2;
    const availableHeight = height - SCREEN_MARGIN_PX * 2;
    const fitScale = Math.min(availableWidth / effectiveWidthPx, availableHeight / effectiveHeightPx, MAX_SCALE);
    const clampedScale = Math.max(MIN_SCALE, fitScale);

    setScale(clampedScale);
    setStagePos({
      x: (width - effectiveWidthPx * clampedScale) / 2,
      y: (height - effectiveHeightPx * clampedScale) / 2,
    });
  }, [width, height, effectiveWidthPx, effectiveHeightPx]);

  // Smarter default zoom: fit the room comfortably on first mount and
  // whenever the room itself changes (different wedding), rather than
  // always starting at a flat 100% that might overflow a laptop screen
  // for a large room or look tiny for a small one. Does NOT re-fit on
  // every resize after that, and does NOT re-fit on rotation toggle —
  // rotation has its own coordinated tween (below) that adjusts pan/zoom
  // smoothly together with the rotation itself, which feels far more
  // natural than an instant re-fit snap fighting an animated rotation.
  useEffect(() => {
    hasAutoFitRef.current = false;
  }, [room.room]);

  useEffect(() => {
    if (hasAutoFitRef.current) return;
    if (width <= 0 || height <= 0) return;
    fitToScreen();
    hasAutoFitRef.current = true;
  }, [width, height, fitToScreen]);

  const handleStageClick = useCallback(
    (e: Konva.KonvaEventObject<Event>) => {
      // Clicking the stage itself (not a shape) clears selection.
      if (e.target === e.target.getStage()) {
        clearSelection();
        return;
      }

      // Shift-click (or its touch equivalent isn't really a thing, but
      // shift+tap on a trackpad-driven tablet still fires shiftKey) adds
      // the clicked shape to the current selection instead of replacing
      // it — this is the multi-select entry point. Individual shapes
      // still call onSelect() with no knowledge of modifier keys; we
      // intercept here at the Stage level since that's where the raw
      // browser event with shiftKey actually lives.
      const evt = e.evt as MouseEvent | undefined;
      if (evt && 'shiftKey' in evt && evt.shiftKey) {
        const id = e.target.id();
        if (id) {
          e.cancelBubble = true;
          toggleSelection(id);
        }
      }
    },
    [clearSelection, toggleSelection]
  );

  const handleWheel = useCallback((e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = direction > 0 ? oldScale * SCALE_STEP : oldScale / SCALE_STEP;
    const clampedScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale));

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    setScale(clampedScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    });
  }, []);

  function zoomBy(factor: number) {
    const stage = stageRef.current;
    if (!stage) {
      setScale((s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * factor)));
      return;
    }

    // Zoom toward the center of the visible canvas, not the top-left
    // corner — zooming "toward nothing in particular" is a common
    // developer-tool-feeling default; zooming toward the center of what
    // the coordinator is currently looking at feels intentional.
    const oldScale = stage.scaleX();
    const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, oldScale * factor));
    const centerX = width / 2;
    const centerY = height / 2;
    const pointTo = {
      x: (centerX - stage.x()) / oldScale,
      y: (centerY - stage.y()) / oldScale,
    };
    const targetX = centerX - pointTo.x * newScale;
    const targetY = centerY - pointTo.y * newScale;

    new Konva.Tween({
      node: stage,
      duration: 0.15,
      easing: Konva.Easings.EaseOut,
      scaleX: newScale,
      scaleY: newScale,
      x: targetX,
      y: targetY,
      onFinish: () => {
        setScale(newScale);
        setStagePos({ x: targetX, y: targetY });
      },
    }).play();
  }

  function resetZoom() {
    // "Reset zoom" returns to a literal 100% (1 room-foot = FEET_TO_PX
    // pixels), centered — distinct from "fit to screen," which scales to
    // whatever makes the whole room visible. Coordinators asked for both
    // explicitly, so both exist as separate, clearly-labeled actions.
    setScale(1);
    setStagePos({
      x: (width - effectiveWidthPx) / 2,
      y: (height - effectiveHeightPx) / 2,
    });
  }

  // Keyboard shortcuts for zoom (+ / - / 0 / f for fit-to-screen) — kept
  // local to this component rather than the shared useEditorShortcuts
  // hook, since zoom is a canvas-view concern, not an editor-data
  // concern. Latest handlers are read through a ref so this effect's
  // listener never needs to be torn down and re-attached on every
  // render (zoomBy/resetZoom/fitToScreen are plain functions, recreated
  // each render, so referencing them directly in a dependency array
  // would otherwise cause that needless churn).
  const latestHandlersRef = useRef({ zoomBy, resetZoom, fitToScreen });
  latestHandlersRef.current = { zoomBy, resetZoom, fitToScreen };

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        latestHandlersRef.current.zoomBy(1.2);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        latestHandlersRef.current.zoomBy(1 / 1.2);
      } else if (e.key === '0') {
        e.preventDefault();
        latestHandlersRef.current.resetZoom();
      } else if (e.key === 'f' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        latestHandlersRef.current.fitToScreen();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Rotated rooms are drawn via a Group rotated 90 degrees around the
  // room's own center, then re-centered in the stage. The room's
  // unrotated top-left-origin content (background + objects) is drawn
  // inside that group exactly as it always is — nothing about how
  // RoomBackground or ObjectRenderer work needs to know rotation exists.
  const rotationDegrees = orientation === 'rotated' ? 90 : 0;
  const rotationOffsetX = orientation === 'rotated' ? roomWidthPx / 2 : 0;
  const rotationOffsetY = orientation === 'rotated' ? roomHeightPx / 2 : 0;
  // After rotating around the room's center, the rotated bounding box is
  // centered at the same point the unrotated one was — so we translate
  // that shared center to the middle of its (now swapped) effective footprint.
  const groupX = orientation === 'rotated' ? effectiveWidthPx / 2 : 0;
  const groupY = orientation === 'rotated' ? effectiveHeightPx / 2 : 0;

  // Animate the rotation transition smoothly rather than letting it snap
  // instantly — this is the single biggest lever for "room rotation feels
  // natural" rather than jarring. Konva.Tween animates the actual node
  // properties directly (not via React re-renders every frame), so this
  // stays smooth regardless of how many objects are on the canvas.
  //
  // Known tradeoff: react-konva also re-applies these same props (groupX,
  // rotationDegrees, etc.) on every render via its normal prop-diffing.
  // If an unrelated re-render happens to land mid-tween, the animation
  // will visually truncate and snap to its final position early rather
  // than completing its smooth arc — but it always lands on the correct
  // final value either way, so this never produces a wrong end state,
  // only an occasionally-shortened animation. Acceptable given how much
  // more natural the common case (a single rotation toggle with no
  // concurrent edits) feels as a result.
  useEffect(() => {
    if (displayedOrientationRef.current === orientation) return;
    displayedOrientationRef.current = orientation;

    const targets = { x: groupX, y: groupY, rotation: rotationDegrees, offsetX: rotationOffsetX, offsetY: rotationOffsetY };

    for (const nodeRef of [backgroundGroupRef, objectsGroupRef]) {
      const node = nodeRef.current;
      if (!node) continue;
      new Konva.Tween({
        node,
        duration: ROTATION_TWEEN_DURATION_SEC,
        easing: Konva.Easings.EaseInOut,
        ...targets,
      }).play();
    }

    // Coordinate the zoom/pan with the rotation: recompute what "fit to
    // screen" would be for the NEW effective footprint, and tween the
    // Stage itself there too, on the same timing — so the whole room
    // smoothly rotates AND resizes-to-fit together as one motion, rather
    // than rotating in place and then separately snapping zoom level.
    const stage = stageRef.current;
    if (stage && width > 0 && height > 0) {
      const availableWidth = width - SCREEN_MARGIN_PX * 2;
      const availableHeight = height - SCREEN_MARGIN_PX * 2;
      const fitScale = Math.min(availableWidth / effectiveWidthPx, availableHeight / effectiveHeightPx, MAX_SCALE);
      const clampedScale = Math.max(MIN_SCALE, fitScale);
      const targetX = (width - effectiveWidthPx * clampedScale) / 2;
      const targetY = (height - effectiveHeightPx * clampedScale) / 2;

      new Konva.Tween({
        node: stage,
        duration: ROTATION_TWEEN_DURATION_SEC,
        easing: Konva.Easings.EaseInOut,
        x: targetX,
        y: targetY,
        scaleX: clampedScale,
        scaleY: clampedScale,
        onFinish: () => {
          // Sync React state to match where the tween actually landed,
          // so subsequent renders (and the zoom-percentage readout) agree
          // with the Stage's real, current values rather than going stale.
          setScale(clampedScale);
          setStagePos({ x: targetX, y: targetY });
        },
      }).play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when orientation itself changes; every other value used here is freshly derived each render from props/state that don't need to independently re-trigger this effect
  }, [orientation]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-evergreen-50">
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        scaleX={scale}
        scaleY={scale}
        x={stagePos.x}
        y={stagePos.y}
        draggable
        onClick={handleStageClick}
        onTap={handleStageClick}
        onWheel={handleWheel}
        onDragEnd={(e) => {
          // Only treat this as a stage pan if the stage itself moved
          // (not a child shape bubbling a drag event up).
          if (e.target === e.target.getStage()) {
            setStagePos({ x: e.target.x(), y: e.target.y() });
          }
        }}
      >
        <Layer>
          <Group
            ref={backgroundGroupRef}
            x={groupX}
            y={groupY}
            rotation={rotationDegrees}
            offsetX={rotationOffsetX}
            offsetY={rotationOffsetY}
          >
            <RoomBackground room={room} />
          </Group>
        </Layer>

        <Layer>
          <Group
            ref={objectsGroupRef}
            x={groupX}
            y={groupY}
            rotation={rotationDegrees}
            offsetX={rotationOffsetX}
            offsetY={rotationOffsetY}
          >
            {objects
              .slice()
              .sort((a, b) => a.zIndex - b.zIndex)
              .map((obj) => (
                <ObjectRenderer key={obj.id} objectId={obj.id} catalog={catalog} roomRotationDegrees={rotationDegrees} />
              ))}
            <SelectionTransformer stageRef={stageRef} />
          </Group>
        </Layer>
      </Stage>

      {/* Zoom controls — simple, always visible, thumb-friendly on tablet. */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-1 rounded-lg bg-white/95 p-1 shadow-card">
        <button
          aria-label="Zoom in"
          title="Zoom in (or press +)"
          onClick={() => zoomBy(1.2)}
          className="flex h-9 w-9 items-center justify-center rounded font-body text-lg text-ink-600 hover:bg-ivory-200"
        >
          +
        </button>
        <button
          aria-label="Zoom out"
          title={'Zoom out (or press \u2212)'}
          onClick={() => zoomBy(1 / 1.2)}
          className="flex h-9 w-9 items-center justify-center rounded font-body text-lg text-ink-600 hover:bg-ivory-200"
        >
          {'\u2212'}
        </button>
        <div className="my-0.5 h-px bg-ivory-200" />
        <button
          aria-label="Fit to screen"
          title="Fit to screen"
          onClick={fitToScreen}
          className="flex h-9 w-9 items-center justify-center rounded font-body text-xs text-ink-600 hover:bg-ivory-200"
        >
          {'\u26F6'}
        </button>
        <button
          aria-label={`Current zoom ${Math.round(scale * 100)} percent \u2014 click to reset to 100 percent`}
          title="Click to reset zoom to 100% (or press 0)"
          onClick={resetZoom}
          className="flex h-9 w-9 items-center justify-center rounded font-body text-xs text-ink-500 underline-offset-2 hover:bg-ivory-200 hover:underline"
        >
          {Math.round(scale * 100)}%
        </button>
      </div>

      <p className="absolute bottom-4 left-4 font-body text-xs text-ink-400">
        {room.displayName} {'\u00b7'} {room.widthFt}&apos; {'\u00d7'} {room.lengthFt}&apos;
        {orientation === 'rotated' ? ' \u00b7 landscape view' : ''}
      </p>
    </div>
  );
}
